import { analytics, IDENTITY_CHANGED, type SemanticContext } from '@/analytics/client';
import {
  beginAction,
  beginWorkflow,
  captureExecution,
  capturePageExecution,
  createContextId,
  currentAccountScope,
  sameAccount,
  type RequestAnalyticsContext,
} from '@/analytics/context';
import type {
  AnalyticsExecutionSnapshot,
  AnalyticsSurface,
  EntityReference,
} from '@/analytics/contract-v2';
import {
  resultCount,
  resultsPageForResponse,
  resultsPageFromCache,
  type AppliedSearch,
  type ResultsPage,
  type SearchPageRequest,
} from '@/analytics/features/search-ui';
import type { SemanticDetailOptions } from '@/analytics/observer';
import { useEffect, useRef, useState } from 'react';
import { productMatchEvidence } from './researchAnalytics';

export interface ResearchView {
  readonly id?: string;
  readonly context?: SemanticContext & { surface: AnalyticsSurface };
  readonly execution?: AnalyticsExecutionSnapshot;
  readonly resource: EntityReference;
  readonly resultPosition?: number;
}

/** Business scope also works with collection disabled. Account epochs reject logout/return. */
export function useResearchAccount(user: any): string {
  const [, update] = useState(0);
  useEffect(() => {
    const changed = () => update((value) => value + 1);
    window.addEventListener(IDENTITY_CHANGED, changed);
    return () => window.removeEventListener(IDENTITY_CHANGED, changed);
  }, []);
  const scope = currentAccountScope();
  return JSON.stringify([user?.id, user?.client?.id, scope?.accountEpoch]);
}

export function startResearchView(
  resource: EntityReference,
  surface: AnalyticsSurface,
  inherited?: SemanticContext,
  resultPosition?: number,
  interactive = false,
): ResearchView {
  const stale =
    inherited?.accountScope && !sameAccount(inherited.accountScope, currentAccountScope());
  let context: SemanticContext | undefined = stale ? undefined : inherited;
  if (!stale && !context?.workflow_id) {
    const workflow = beginWorkflow({
      type: surface === 'product_detail_drawer' ? 'product_discovery' : 'company_research',
      surface,
      parentEntity: resource,
      origin: context,
      reset: surface !== 'company_detail',
      companyType:
        resource.namespace === 'internal_marketer'
          ? 'marketer'
          : resource.namespace === 'internal_manufacturer'
          ? 'manufacturer'
          : undefined,
    });
    if (workflow) {
      context = workflow;
      analytics.trackSemantic(
        'workflow_started',
        workflow,
        {
          surface,
          workflow_id: workflow.workflow_id,
          workflow_type: workflow.workflow_type,
          origin_page_view_id: workflow.origin_page_view_id,
        },
        { factKey: workflow.workflow_id, executionKind: interactive ? 'interactive' : 'automatic' },
      );
    }
  }
  if (context && interactive) {
    const action = beginAction({ surface, context });
    // Capturing an accepted action renews activity; an automatic detail effect never does.
    if (action) {
      captureExecution({ executionKind: 'interactive', action });
      context = { ...context, ...action };
    }
  }
  const scoped = context ? { ...context, surface } : undefined;
  return {
    id: createContextId(),
    context: scoped,
    resource,
    resultPosition,
    execution: scoped ? capturePageExecution(scoped) : undefined,
  };
}

/** The owned visible instance retains its identity across effect replay and rerenders. */
export function useResearchView(
  open: boolean,
  scope: string,
  resource: EntityReference | undefined,
  surface: AnalyticsSurface,
  inherited?: SemanticContext,
  resultPosition?: number,
): ResearchView | undefined {
  const key = `${scope}:${resource?.namespace}:${resource?.id}:${inherited?.workflow_id || ''}`;
  const held = useRef<{ key: string; view: ResearchView }>();
  const [current, setCurrent] = useState<typeof held.current>();
  useEffect(() => {
    if (!open || !resource) {
      held.current = undefined;
      setCurrent(undefined);
    } else if (held.current?.key !== key) {
      held.current = { key, view: startResearchView(resource, surface, inherited, resultPosition) };
      setCurrent(held.current);
    }
  }, [open, key]);
  return open && current?.key === key ? current.view : undefined;
}

export function detailOptions(
  view: ResearchView | undefined,
  status: 'loading' | 'ready' | 'failed',
  execution = view?.execution,
  loadSource: 'network' | 'cache' = 'network',
): SemanticDetailOptions | undefined {
  return view?.id && view.context
    ? {
        viewId: view.id,
        context: view.context,
        resource: view.resource,
        resultPosition: view.resultPosition,
        status,
        execution,
        loadSource,
        ...(status === 'failed' ? { failureReason: 'unavailable' } : {}),
      }
    : undefined;
}

/** Each read captures its own operation. Failure can retain the same initiating operation. */
export function researchRequest(
  view: ResearchView | undefined,
  trigger: 'initial_load' | 'retry' = 'initial_load',
) {
  let analyticsContext: RequestAnalyticsContext | undefined;
  if (view?.context && view.execution) {
    const action =
      trigger === 'retry'
        ? beginAction({ surface: view.context.surface, context: view.context, trigger })
        : undefined;
    analyticsContext = action
      ? { executionKind: 'interactive', action, trigger }
      : {
          executionKind: 'automatic',
          execution: view.execution,
          attribution: view.context,
          trigger,
        };
  }
  const execution = analyticsContext ? captureExecution(analyticsContext) : undefined;
  return {
    analyticsContext: execution
      ? { ...analyticsContext!, operationId: execution.operationId }
      : analyticsContext,
    execution,
  };
}

export function revisitResearchPage(page: ResultsPage, request: SearchPageRequest): ResultsPage {
  const visit = resultsPageFromCache(page, request);
  return request.analyticsContext?.executionKind === 'automatic'
    ? {
        ...visit,
        execution: request.analyticsContext.execution,
        context: { ...visit.context, execution_kind: 'automatic' },
      }
    : visit;
}

export function unavailableResearchPage(
  run: AppliedSearch<any>,
  request: SearchPageRequest,
  execution?: AnalyticsExecutionSnapshot,
): ResultsPage {
  const page = resultsPageForResponse(run, request, undefined, {
    ...resultCount(null, 'unavailable'),
    displayStatus: 'failed',
  });
  return { ...page, execution, context: { ...page.context, operation_id: execution?.operationId } };
}

export function inspectProductMatch(view: ResearchView | undefined, product: any) {
  if (
    !view?.id ||
    !view.context?.parent_entity ||
    view.context.parent_entity.namespace === 'internal_product'
  )
    return;
  const action = beginAction({ surface: view.context.surface, context: view.context });
  if (!action) return;
  const evidence = productMatchEvidence(product);
  analytics.trackSemantic(
    'portfolio_match_inspected',
    { ...view.context, ...action },
    {
      surface: view.context.surface,
      resource: view.resource,
      parent_entity: view.context.parent_entity,
      detail_view_id: view.id,
      match_dimensions: evidence.dimensions,
      match_status: evidence.status,
      properties_truncated: evidence.truncated,
    },
    { factKey: action.action_id, executionKind: 'interactive' },
  );
}

export function useCompanyTab(
  visible: boolean,
  tab: 'products' | 'people' | 'locations',
  view: ResearchView | undefined,
  userTab: React.MutableRefObject<string | undefined>,
) {
  const previous = useRef<{ view?: ResearchView; tab: typeof tab }>();
  useEffect(() => {
    if (
      !visible ||
      !view ||
      (previous.current && previous.current.view?.id === view.id && previous.current.tab === tab)
    )
      return;
    const prior =
      previous.current && previous.current.view?.id === view.id ? previous.current.tab : null;
    const reason = prior ? (userTab.current === tab ? 'user' : 'programmatic') : 'default';
    userTab.current = undefined;
    previous.current = { view, tab };
    const id = createContextId();
    const action =
      reason === 'user' && view.context
        ? beginAction({ surface: view.context.surface, context: view.context })
        : undefined;
    if (id && view.context)
      analytics.trackSemantic(
        'company_tab_viewed',
        { ...view.context, ...action },
        {
          surface: view.context.surface,
          resource: view.resource,
          tab,
          previous_tab: prior,
          tab_view_id: id,
          view_reason: reason,
        },
        { factKey: id, executionKind: reason === 'user' ? 'interactive' : 'automatic' },
      );
  }, [visible, tab, view]);
}
